import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkle, VideoCamera, MapPin, ArrowsClockwise } from "@phosphor-icons/react";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CategorySelect } from "./CategorySelect";
import { SuggestInput } from "./SuggestInput";
import { MediaDropzone, UploadQueue } from "./MediaDropzone";
import { useUploadQueue } from "./useUploadQueue";
import { TranscribeUploadModal } from "./TranscribeUploadModal";
import { newDraftId, shrinkImage } from "./serviceApi";
import { EquipmentSuggestions } from "./EquipmentSuggestions";
import { RecurringFields, recurringMissing, scheduleBody } from "./recurring/RecurringFields";
import { useScheduleSaver } from "./recurring/useScheduleSaver";
import { blankSchedule } from "./recurring/useRecurring";

const pad = (n) => String(n).padStart(2, "0");
const blank = (propertyId) => {
  const d = new Date();
  return { property_id: propertyId || "", service_date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    service_time: `${pad(d.getHours())}:${pad(d.getMinutes())}`, unit: "", category_id: "", title: "",
    service_person_name: "", notes: "", status: "draft", equipment_brand: "", equipment_serial: "", equipment_model: "" };
};

export function RecordModal({ open, onOpenChange, client, record, defaultPropertyId, properties, fixedProperty, categories, onCategoryAdded, onSaved, rec, onScheduleSaved }) {
  const [form, setForm] = useState(blank());
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [draftId, setDraftId] = useState(newDraftId);
  const [pending, setPending] = useState([]);
  const [autoCaption, setAutoCaption] = useState(false);
  const [tuOpen, setTuOpen] = useState(false);
  const [takeSerial, setTakeSerial] = useState(false);
  const [takeModel, setTakeModel] = useState(false);
  const [scan, setScan] = useState(null);
  const [scanPhotos, setScanPhotos] = useState([]);
  const [svcType, setSvcType] = useState("one_time");
  const [recV, setRecV] = useState(blankSchedule);
  const schedDone = useRef(false);
  const saver = useScheduleSaver();
  const canRecur = !record && !client.portal && !!rec?.status?.can_manage;
  const queue = useUploadQueue();
  const recId = record?.id || savedId;

  useEffect(() => {
    if (!open) return;
    const f = record ? { ...blank(), ...Object.fromEntries(Object.keys(blank()).map((k) => [k, record[k] ?? ""])) } : blank(fixedProperty?.id || defaultPropertyId);
    setForm(f); setInitial(JSON.stringify(f)); queue.reset(); setSavedId(null); setPending([]); setDraftId(newDraftId()); setScan(null); setTakeSerial(false); setTakeModel(false); setSvcType("one_time"); setRecV(blankSchedule()); schedDone.current = false;
  }, [open, record, defaultPropertyId, fixedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(form) !== initial || queue.items.some((i) => i.status !== "done") || pending.length > 0;
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const missing = useMemo(() => [
    !form.property_id && "property", !form.category_id && "category", !form.title.trim() && "service title",
  ].filter(Boolean), [form]);
  const catName = categories.find((c) => c.id === form.category_id)?.name || "";

  useEffect(() => {
    if (!open || !dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [open, dirty]);

  const close = (discard) => {
    if (discard && pending.length) client.discardDraft(draftId);
    onOpenChange(false);
  };
  const requestClose = (v) => {
    if (v) return;
    if (dirty && !saving) setConfirmClose(true); else close(true);
  };

  const switchType = (k) => {
    if (k === svcType) return;
    if (k === "one_time" && (recV.due_date || recV.contact_ids.length || recV.group_ids.length) && !window.confirm("Discard the recurring schedule settings? No reminders will be set up.")) return;
    if (k === "one_time") setRecV(blankSchedule());
    setSvcType(k);
  };

  const runScan = async (files) => {
    const photos = files.filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name)).slice(0, 6);
    if (!photos.length) return;
    setScanPhotos(photos.map((f) => URL.createObjectURL(f)));
    setScan({ status: "loading" });
    try {
      const blobs = await Promise.all(photos.map((f) => shrinkImage(f)));
      const r = await client.scanEquipment(blobs);
      setScan({ status: "done", candidates: r.candidates || [] });
    } catch (e) {
      setScan({ status: "error", error: apiErrorMessage(e, "Couldn't read the photos") });
    }
  };

  const applyNotes = (text, mode, media) => {
    const block = `[AI summary from video · ${media.filename}]\n${text}`;
    setForm((f) => ({ ...f, notes: mode === "replace" || !f.notes.trim() ? block : `${f.notes.trimEnd()}\n\n${block}` }));
  };

  const save = async (status) => {
    if (missing.length || !form.service_date) { toast.error(`Please fill in: ${missing.join(", ") || "service date"}`); return; }
    const recurring = canRecur && svcType === "recurring";
    let lateChoice = null;
    if (recurring && !schedDone.current) {
      const miss = recurringMissing(recV);
      if (miss.length) { toast.error(`Recurring schedule needs: ${miss.join(", ")}`); return; }
      try { lateChoice = await saver.check(scheduleBody(recV)); } catch (e) { toast.error(apiErrorMessage(e, "Check the recurring schedule")); return; }
      if (!lateChoice) return;
    }
    setSaving(true);
    try {
      const body = { ...form, status: status || form.status, draft_id: pending.length ? draftId : null };
      const data = recId && !client.portal ? await client.updateRecord(recId, body) : recId ? { id: recId, property_id: form.property_id } : await client.createRecord(body);
      setSavedId(data.id); setPending([]);
      if (recurring && !schedDone.current) {
        try { await saver.save({ ...scheduleBody(recV), ...lateChoice, record_id: data.id }); schedDone.current = true; onScheduleSaved?.(); }
        catch (e) { toast.error(apiErrorMessage(e, "Entry saved, but the recurring schedule could not be created — use Make recurring on the entry")); }
      }
      const failed = await queue.runAll(client, data.id, queue.items);
      if (failed) {
        toast.error(`${failed} upload(s) failed — retry them below`);
        setInitial(JSON.stringify(body));
        onSaved?.(data, { keepOpen: true });
        return;
      }
      toast.success(client.portal ? "Service entry submitted" : record ? "Service entry saved" : "Service entry created");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not save service entry"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={requestClose}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="record-modal">
          <DialogHeader><DialogTitle>{record ? "Edit service entry" : "New service entry"}</DialogTitle></DialogHeader>
          {canRecur && (
            <div className="flex items-center gap-2" data-testid="record-service-type">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Service type</span>
              <div className="flex bg-slate-100 rounded-full p-0.5">
                {[["one_time", "One-Time"], ["recurring", "Recurring"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => switchType(k)} data-testid={`record-service-type-${k}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${svcType === k ? (k === "recurring" ? "bg-orange-500 text-white shadow-sm" : "bg-white text-slate-900 shadow-sm") : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-6">
              <Label>Property address *</Label>
              {fixedProperty ? (
                <div className="flex items-center gap-1.5 h-10 px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700" data-testid="record-property-fixed">
                  <MapPin size={14} className="text-[#327de3]" /> {fixedProperty.full_address}
                </div>
              ) : (
                <Select value={form.property_id} onValueChange={set("property_id")} disabled={pending.length > 0}>
                  <SelectTrigger data-testid="record-property-select"><SelectValue placeholder="Choose property" /></SelectTrigger>
                  <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_address || p.street}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="col-span-3 sm:col-span-2">
              <Label>Service date *</Label>
              <Input type="date" value={form.service_date} onChange={set("service_date")} data-testid="record-date-input" />
            </div>
            <div className="col-span-3 sm:col-span-2">
              <Label>Service time</Label>
              <Input type="time" value={form.service_time} onChange={set("service_time")} data-testid="record-time-input" />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label>Apartment / unit</Label>
              <SuggestInput value={form.unit} onChange={set("unit")} deps={[form.property_id]} placeholder="e.g. 402, Basement" testId="record-unit-input"
                fetchItems={(q) => (form.property_id ? client.units(form.property_id, q) : Promise.resolve([]))} />
            </div>
            <div className="col-span-6">
              <Label>Service category *</Label>
              <CategorySelect client={client} categories={categories} value={form.category_id} onChange={set("category_id")} onCategoryAdded={onCategoryAdded} />
            </div>
            <div className="col-span-6 sm:col-span-4">
              <Label>Service title *</Label>
              <Input value={form.title} onChange={set("title")} placeholder="e.g. Boiler Service" data-testid="record-title-input" />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label>Service person</Label>
              <SuggestInput value={form.service_person_name} onChange={set("service_person_name")} placeholder="e.g. Ryan Friedman" testId="record-person-input"
                fetchItems={(q) => client.people(q).then((l) => l.map((p) => p.display_name))} />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label>Brand / Manufacturer</Label>
              <Input value={form.equipment_brand} onChange={set("equipment_brand")} placeholder="Optional — e.g. Rheem" data-testid="record-brand-input" />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label>Model Number</Label>
              <Input value={form.equipment_model} onChange={set("equipment_model")} placeholder="Optional" className="font-mono" data-testid="record-model-input" />
            </div>
            <div className="col-span-6 sm:col-span-2">
              <Label>Serial Number</Label>
              <Input value={form.equipment_serial} onChange={set("equipment_serial")} placeholder="Optional" className="font-mono" data-testid="record-serial-input" />
            </div>
            <div className="col-span-6">
              <Label>Service notes</Label>
              <Textarea rows={5} value={form.notes} onChange={set("notes")} placeholder="What was done, findings, follow-ups…" data-testid="record-notes-input" />
            </div>
            {canRecur && svcType === "recurring" && (
              <div className="col-span-6 rounded-lg border border-orange-200 bg-orange-50/40 p-3 animate-in fade-in slide-in-from-top-2 duration-300" data-testid="record-recurring-section">
                <div className="flex items-center gap-1.5 mb-2 text-sm font-bold text-orange-700"><ArrowsClockwise size={15} weight="bold" /> Recurring Schedule & Notifications</div>
                <RecurringFields value={recV} onChange={setRecV} status={rec.status} contacts={rec.contacts} groups={rec.groups} />
              </div>
            )}
            <div className="col-span-6 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="mb-0">Photos & videos</Label>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer" data-testid="record-take-serial-label">
                    <Checkbox checked={takeSerial} onCheckedChange={(v) => setTakeSerial(!!v)} data-testid="record-take-serial-checkbox" /> Take Serial Number From Photos
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer" data-testid="record-take-model-label">
                    <Checkbox checked={takeModel} onCheckedChange={(v) => setTakeModel(!!v)} data-testid="record-take-model-checkbox" /> Take Model Number From Photos
                  </label>
                  <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer" data-testid="record-auto-caption-label">
                    <Checkbox checked={autoCaption} onCheckedChange={(v) => setAutoCaption(!!v)} data-testid="record-auto-caption-checkbox" /> Auto Caption videos
                  </label>
                  <Button type="button" size="sm" onClick={() => setTuOpen(true)} disabled={!form.property_id} className="h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid="record-upload-transcribe-btn">
                    <Sparkle size={13} weight="fill" className="mr-1" /> Upload & Transcribe
                  </Button>
                </div>
              </div>
              <MediaDropzone testId="record-dropzone" disabled={missing.length > 0}
                disabledReason={`Fill in the ${missing.join(", ")} first — photos are stamped with these details.`}
                onFiles={(files) => { queue.add(files, { autoCaption }); if (takeSerial || takeModel) runScan(files); }} />
              <EquipmentSuggestions scan={scan} photos={scanPhotos} form={form} useSerial={takeSerial} useModel={takeModel} onDismiss={() => setScan(null)}
                onApply={(v, replace) => setForm((f) => {
                  const pick = (k) => (k in v && (replace || !f[k]) ? (v[k] || f[k]) : f[k]);
                  return { ...f, equipment_brand: pick("equipment_brand"), equipment_serial: pick("equipment_serial"), equipment_model: pick("equipment_model") };
                })} />
              {pending.length > 0 && (
                <div className="space-y-1" data-testid="record-pending-videos">
                  {pending.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 text-sm px-2 py-1.5 rounded-md border border-violet-200 bg-violet-50/40">
                      <VideoCamera size={15} className="text-violet-600" />
                      <span className="flex-1 truncate text-slate-700">{m.filename}</span>
                      <span className="text-[10px] font-bold uppercase text-violet-600">attached on save</span>
                    </div>
                  ))}
                </div>
              )}
              <UploadQueue items={queue.items} onRemove={queue.remove} onRetry={recId ? (it) => queue.runOne(client, recId, it) : undefined} />
              {queue.items.length > 0 && <div className="text-[11px] text-slate-500">Files upload when you save{autoCaption ? " · videos will be auto-captioned" : ""}.</div>}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => requestClose(false)} data-testid="record-cancel-btn">Cancel</Button>
            <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="record-save-draft-btn">{saving ? "Saving…" : "Save as draft"}</Button>
            <Button onClick={() => save("completed")} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="record-save-complete-btn">
              {saving ? "Saving…" : client.portal ? "Submit entry" : "Save as completed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TranscribeUploadModal
        open={tuOpen}
        onOpenChange={setTuOpen}
        client={client}
        target={{ draftId, propertyId: form.property_id }}
        context={{ category_name: catName, title: form.title }}
        notes={form.notes}
        onApplyNotes={applyNotes}
        onUploaded={(m) => setPending((l) => [...l, m])}
      />
      {saver.dialog}
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent data-testid="record-unsaved-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription>You have changes or files that haven't been saved yet. Pending videos will be removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="record-unsaved-keep">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmClose(false); close(true); }} className="bg-rose-600 hover:bg-rose-700" data-testid="record-unsaved-discard">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
